import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, Phone, ShieldCheck } from "lucide-react";
import { SectionHeading } from "./Reveal";
import { WhatsAppIcon } from "./WhatsAppIcon";
import { whatsappUrl } from "@/lib/contact";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  nome: "",
  cognome: "",
  telefono: "",
  email: "",
  modalita: "In presenza (Milano)",
  messaggio: "",
  privacy: false,
};

const modalitaOptions = ["In presenza (Milano)", "Online (FAD)", "Da valutare"];

const inputCls =
  "w-full border border-line bg-white px-4 py-3.5 text-sm text-ink outline-none transition-colors placeholder:text-slate-400 focus:border-navy";

export default function LeadForm() {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.privacy) {
      toast.error("Devi accettare l'informativa privacy per proseguire.");
      return;
    }
    setLoading(true);
    try {
      const { privacy, ...payload } = form;
      await axios.post(`${API}/leads`, payload);
      setSent(true);
      setForm(initial);
      toast.success("Richiesta inviata! Ti ricontatteremo al più presto.");
    } catch (err) {
      toast.error("Errore nell'invio. Riprova o chiamaci al 351 393 7976.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="iscriviti" className="border-t border-line bg-paper py-16 md:py-24" data-testid="lead-form-section">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-10 px-4 sm:px-6 lg:grid-cols-12 lg:px-8">
        <div className="lg:col-span-5">
          <SectionHeading
            eyebrow="Iscrizioni aperte"
            title="Richiedi informazioni o prenota il tuo posto"
            sub="Compila il modulo: ti ricontattiamo entro 24 ore lavorative con calendario, modalità di pagamento e tutto ciò che serve per iscriverti."
            testid="lead-form"
          />
          <div className="mt-8 space-y-4">
            <a
              href="tel:+393513937976"
              className="flex items-center gap-4 border border-line bg-white p-5 transition-colors hover:border-navy"
              data-testid="lead-form-phone-card"
            >
              <span className="flex h-11 w-11 items-center justify-center bg-navy text-white">
                <Phone className="h-5 w-5" />
              </span>
              <span>
                <span className="block font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">Preferisci parlare con noi?</span>
                <span className="block font-heading text-lg font-bold text-navy">+39 351 393 7976</span>
              </span>
            </a>
            <a
              href={whatsappUrl(form.modalita)}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-4 border border-line bg-white p-5 transition-colors hover:border-[#25D366]"
              data-testid="lead-form-whatsapp-card"
            >
              <span className="flex h-11 w-11 items-center justify-center bg-[#25D366] text-white">
                <WhatsAppIcon className="h-5 w-5" />
              </span>
              <span>
                <span className="block font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">Oppure scrivici su</span>
                <span className="block font-heading text-lg font-bold text-navy">WhatsApp</span>
              </span>
            </a>
            <div className="flex items-center gap-3 text-sm text-slate-500" data-testid="lead-form-privacy-note">
              <ShieldCheck className="h-4 w-4 text-brand-amber" />
              I tuoi dati sono usati solo per ricontattarti in merito al corso.
            </div>
          </div>
        </div>

        <div className="lg:col-span-7">
          {sent ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              className="flex h-full flex-col items-center justify-center border border-green-200 bg-green-50 p-12 text-center"
              data-testid="lead-form-success"
            >
              <CheckCircle2 className="h-14 w-14 text-green-600" />
              <h3 className="mt-5 font-heading text-2xl font-bold text-navy">Richiesta inviata!</h3>
              <p className="mt-2 max-w-md text-sm leading-relaxed text-slate-600">
                Grazie per il tuo interesse. Un nostro consulente ti ricontatterà al più presto
                al numero o all'email che hai indicato.
              </p>
              <button
                onClick={() => setSent(false)}
                className="mt-6 border border-navy px-6 py-3 font-heading text-sm font-bold text-navy transition-colors hover:bg-navy hover:text-white"
                data-testid="lead-form-new-request-button"
              >
                Invia un'altra richiesta
              </button>
            </motion.div>
          ) : (
            <form onSubmit={onSubmit} className="border border-line bg-white p-6 sm:p-8" data-testid="lead-form">
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="nome" className="mb-1.5 block text-sm font-semibold text-navy">Nome *</label>
                  <input id="nome" required value={form.nome} onChange={set("nome")} placeholder="Es. Mario" className={inputCls} data-testid="input-nome" />
                </div>
                <div>
                  <label htmlFor="cognome" className="mb-1.5 block text-sm font-semibold text-navy">Cognome *</label>
                  <input id="cognome" required value={form.cognome} onChange={set("cognome")} placeholder="Es. Rossi" className={inputCls} data-testid="input-cognome" />
                </div>
                <div>
                  <label htmlFor="telefono" className="mb-1.5 block text-sm font-semibold text-navy">Telefono *</label>
                  <input id="telefono" type="tel" required value={form.telefono} onChange={set("telefono")} placeholder="Es. 340 123 4567" className={inputCls} data-testid="input-telefono" />
                </div>
                <div>
                  <label htmlFor="email" className="mb-1.5 block text-sm font-semibold text-navy">Email *</label>
                  <input id="email" type="email" required value={form.email} onChange={set("email")} placeholder="Es. mario.rossi@email.it" className={inputCls} data-testid="input-email" />
                </div>
              </div>

              <div className="mt-5">
                <span className="mb-2 block text-sm font-semibold text-navy">Modalità preferita *</span>
                <div className="flex flex-wrap gap-2" data-testid="modalita-group">
                  {modalitaOptions.map((opt) => (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, modalita: opt }))}
                      className={`border px-4 py-2.5 text-sm font-semibold transition-colors ${
                        form.modalita === opt
                          ? "border-navy bg-navy text-white"
                          : "border-line bg-white text-slate-600 hover:border-navy hover:text-navy"
                      }`}
                      data-testid={`radio-${opt === "In presenza (Milano)" ? "presenza" : opt === "Online (FAD)" ? "online" : "da-valutare"}`}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mt-5">
                <label htmlFor="messaggio" className="mb-1.5 block text-sm font-semibold text-navy">
                  Messaggio o domande <span className="font-normal text-slate-400">(opzionale)</span>
                </label>
                <textarea
                  id="messaggio"
                  rows={4}
                  value={form.messaggio}
                  onChange={set("messaggio")}
                  placeholder="Richiedi dettagli su orari, rateizzazione o calendario..."
                  className={`${inputCls} resize-none`}
                  data-testid="textarea-messaggio"
                />
              </div>

              <label className="mt-5 flex cursor-pointer items-start gap-3 text-sm text-slate-600" data-testid="privacy-label">
                <input
                  type="checkbox"
                  checked={form.privacy}
                  onChange={set("privacy")}
                  className="mt-0.5 h-4 w-4 accent-[#0F2942]"
                  data-testid="checkbox-privacy"
                />
                <span>
                  Accetto l'
                  <a
                    href="/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-semibold text-navy underline underline-offset-2 hover:text-brand-amber"
                    data-testid="privacy-link"
                  >
                    informativa sulla privacy
                  </a>{" "}
                  per il trattamento dei dati personali *
                </span>
              </label>

              <button
                type="submit"
                disabled={loading}
                className="mt-6 flex w-full items-center justify-center gap-2 bg-brand-amber px-6 py-4 font-heading text-sm font-bold uppercase tracking-wider text-white transition-colors hover:bg-navy disabled:opacity-60"
                data-testid="lead-form-submit-button"
              >
                {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                {loading ? "Invio in corso..." : "Invia richiesta"}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
